import { gql } from 'graphql-request';
import { ThemeFragment } from '@/features/styles/fragments/Theme';
import { LayoutDefinitionFragment } from './LayoutDefinition';

export const PageExperienceFragment = gql`
  ${ThemeFragment}
  ${LayoutDefinitionFragment}

  fragment PageExperience on Experience {
    id
    organizationId
    org {
      id
      type
      seatTier
      effectiveTier
      subscriptionPlan
    }
    type
    kind
    title
    updatedAt
    publishedSlug
    role
    description
    descriptionEnabled
    isCopyable
    isPublic
    isSaved
    # Distinct from the "every current event is added" aggregate: an add-all
    # is a one-shot, so only this says new events will follow.
    viewerCalendarSubscriptions {
      id
      experienceId
      platform
      integrationId
      calendarId
      lastSyncedAt
    }
    layoutId
    layout {
      ...LayoutDefinition
    }
    theme {
      ...Theme
    }
    bannerDisplay
    bannerImageType
    bannerImageUrl
    heroDisplay
    heroImageType
    heroImageUrl
    heroPreviewImageUrl
    logoDisplay
    logoImageType
    logoImageUrl
    attributionDisplay
    weekStartDay
    eventCardLayoutId
    eventCardDateEnabled
    eventCardEndTimeEnabled
    eventCardStartTimeEnabled
    eventCardYearEnabled
    eventCardEmojiEnabled
    eventCardDescriptionEnabled
    eventCardLocationEnabled
    eventCardCategoryEnabled
    eventCardLabelsEnabled
    eventCardTruncateNamesEnabled
    colorLegendEnabled
    shadeWeekendsEnabled
    muteWeekendDatesEnabled
    datesInPrimaryColor
    highlightToday
    headerTitleEnabled
    headerTitleSize
    eventTextSize
    timeFormat
    subscribeEnabled
    eventListDisplay
    eventListStartDate
    eventListEndDate
    timeZoneDisplay
    hue
    saturation
    lightness
    colorType
    customHue
    customSaturation
    customLightness
    customAlpha
    bold
    sparkle
    styleId
    style
    colorPalette
    colorMode
    primaryColor
    vibe
    accentBackgroundEnabled
    timeZone
    owner {
      id
      name
      avatar
    }
    blocks {
      id
      sequence
      type
      layoutId
    }
    # The available interaction apps on an experience
    interactionApps {
      applicationName
      id
      config {
        setting
        value
      }
    }
    interactionSlots {
      id
      blockId
      eventId
      interactionAppId
      slotIndex
    }
    isSubscribed
    chatId
    eventDescriptionFooter
    eventTitlePrefix
    printableConfig {
      background
      backgroundUrl
      isEmojiEnabled
      isEndTimeEnabled
      isLocationEnabled
      isStartTimeEnabled
      isYearEnabled
    }
    config
    fundraiserName
    fundraiserImageUrl
    directoryTags {
      id
      text
      isPublic
    }
    children {
      id
      type
      kind
      # The author's header text. Never a view's name — the pickers derive that
      # from layoutId and kind (see lib/viewName), because a view is a Month or a
      # Grid whatever its header says.
      title
      layoutId
    }
    # The view a fresh visitor lands on, set by an owner or editor. Resolved
    # against children at read time, so an id whose view is gone falls back.
    defaultChildExperienceId
    feeds {
      kind
      id
      title
      originalTitle
      color
      # The feed's default emoji, the middle tier of the emoji rule — see
      # features/events/lib/eventEmoji.lib.ts.
      emoji
      category {
        id
        text
        color
        colorName
        sortOrder
      }
      labels {
        id
        text
        sortOrder
      }
      whoLabels {
        id
        text
        sortOrder
      }
      syncedAt
      error
      # Set when this schedule has lost access to an experience-backed source.
      # The source keeps its row and every setting on it, but delivers no events
      # until access returns.
      accessRevokedAt
      # Set when the origin experience itself was deleted. A revocation on a
      # deleted origin can never be undone, so the panel says so instead of
      # offering to request access back.
      originDeletedAt
      editUrl
      publicUrl
      role
    }
    categories {
      id
      text
      color
      colorName
      sortOrder
    }
    labels {
      id
      text
      sortOrder
    }
    whoLabels {
      id
      text
      sortOrder
    }
  }
`;
